import {
  isOfferCargoVehicleShape,
  isVehicleWeightClass,
} from "@/shared/constants/offer-cargo";
import { VEHICLE_EQIUIPMENTS } from "@/shared/constants/vehicle";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { excludeNull } from "@/shared/lib/collection";
import { safeParseInt } from "@/shared/lib/number";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import type { MasterDataVehicleDetailPageProps } from "../../model";
import {
  type EditMasterDataVehicleFormSchemaType,
  editMasterDataVehicleFormSchema,
} from "../../model/validation";
import { MasterDataVehicleForm } from "./form/master-data-vehicle-form";

export const MasterDataVehicleDetailInProgress = (
  props: MasterDataVehicleDetailPageProps,
) => {
  const links = [
    { href: "/master-data/vehicles", label: "車両管理" },
    { label: "車両詳細・更新" },
  ];

  const { defaultInputValues } = props;
  const { handleSubmit, reset, setValue, ...form } =
    useForm<EditMasterDataVehicleFormSchemaType>({
      resolver: customZodResolver(editMasterDataVehicleFormSchema),
      mode: "onChange",
    });
  useSubscribeFromValues((values) => {
    props.onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  }, form.watch);
  useLoggingEffect(
    () => {
      // vehicleのロードを待って、デフォルト値の設定を行う
      if (props.vehicle.status !== "success") {
        return;
      }
      if (defaultInputValues) {
        reset(defaultInputValues);
        return;
      }

      const weightClass =
        safeParseInt(props.vehicle.data.weightClass) ?? undefined;
      reset({
        name: props.vehicle.data.name ?? undefined,
        number: props.vehicle.data.number ?? undefined,
        officeId: props.vehicle.data.officeId ?? undefined,
        shape: isOfferCargoVehicleShape(props.vehicle.data.shape)
          ? props.vehicle.data.shape
          : undefined,
        weightClass: isVehicleWeightClass(weightClass)
          ? weightClass
          : undefined,
        vehicleEquipments: props.vehicle.data.equipments
          ? excludeNull(
              props.vehicle.data.equipments.map((equipmentId) => {
                return VEHICLE_EQIUIPMENTS.find((eq) => eq.id === equipmentId)
                  ?.value;
              }),
            )
          : undefined,
        memo: props.vehicle.data.memo ?? undefined,
      });
    },
    [props.vehicle.status, props.vehicle.data],
    "MasterDataVehicleDetailInProgress",
  );

  const hasError = Object.keys(form.formState.errors).length > 0;
  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} />
      <Box
        mt={3}
        pb={3}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          車両詳細・更新
        </Typography>
      </Box>
      <Box
        display="flex"
        mx={-4}
        bgcolor="grey.50"
        flexDirection={"column"}
        flexGrow={1}
      >
        <Container maxWidth="md">
          <Stack
            px={{ xs: 2, sm: 0 }}
            pt={{ xs: 2, sm: 6 }}
            pb={{ xs: 4, sm: 4 }}
          >
            {props.vehicle.status === "loading" ||
            props.offices.status === "loading" ? (
              <Stack>
                <CircularProgress />
              </Stack>
            ) : props.vehicle.status === "error" ||
              props.offices.status === "error" ? (
              <BaseMessageCard
                status={"error"}
                message={`${props.vehicle.error?.message ?? ""} ${props.offices.error?.message ?? ""}`}
              />
            ) : (
              <MasterDataVehicleForm
                offices={props.offices.data}
                control={form.control}
              />
            )}
          </Stack>
        </Container>
      </Box>
      <Stack justifyContent="center" alignItems="center" spacing={2} py={2}>
        {hasError ? (
          <BaseMessageCard
            status={"error"}
            message={"必須項目が入力されていない箇所があります。"}
          />
        ) : null}
        <BaseButton
          type="submit"
          disabled={!form.formState.isDirty}
          fullWidth
          variant="contained"
          color="primary"
          sx={{
            maxWidth: "240px",
          }}
          onClick={handleSubmit((data) => {
            props.onConfirmClick(data);
          })}
        >
          確認する
        </BaseButton>
      </Stack>
    </Box>
  );
};
